import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MultiSelect from "@/components/MultiSelect";
import TimeInput from "@/components/TimeInput";
import { formatApiError, useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Upload, X } from "lucide-react";
import { hasRole } from "@/lib/utils";

export default function CreateProgram() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    client_name: "", training_title: "", training_topic: "", batch_size: 0,
    start_date: "", end_date: "", notes: "",
    client_contact: "", client_email: "", estimated_revenue: "", requirements_summary: "",
    technology: [], delivery_mode: "", hours_per_day: "", time_slot_start: "", time_slot_end: "", duration_hours: "",
  });
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [skills, setSkills] = useState([]);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
    api.get("/skills").then(({ data }) => setSkills(data)).catch(() => setSkills([]));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickClient = (name) => {
    const c = clients.find((it) => it.name === name);
    setForm((f) => ({
      ...f,
      client_name: name,
      client_contact: c?.primary_contact || f.client_contact,
      client_email: c?.email || f.client_email,
    }));
  };

  const skillNames = skills.map((s) => s.name);

  const addFiles = (fileList) => {
    setFiles((prev) => [...prev, ...Array.from(fileList)]);
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e, confirmDurationMismatch = false) => {
    e.preventDefault();
    if (!form.client_name) {
      toast.error("Select a client from the directory");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        batch_size: Number(form.batch_size) || 0,
        estimated_revenue: form.estimated_revenue === "" ? null : Number(form.estimated_revenue),
        hours_per_day: form.hours_per_day === "" ? null : Number(form.hours_per_day),
        duration_hours: form.duration_hours === "" ? null : Number(form.duration_hours),
        confirm_duration_mismatch: confirmDurationMismatch,
      };
      const { data } = await api.post("/programs", payload);
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("step_number", "1");
        try {
          await api.post(`/programs/${data.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (e) {
          toast.error(`Failed to attach ${f.name}: ${formatApiError(e.response?.data?.detail)}`);
        }
      }
      toast.success("Program logged. Step 1 complete, PM notified for Faculty Mapping & IT Handoff.");
      nav(`/programs/${data.id}`);
    } catch (err) {
      if (err.response?.status === 422 && typeof err.response?.data?.detail === "string") {
        toast.error(formatApiError(err.response?.data?.detail), {
          action: { label: "Save anyway", onClick: () => submit(e, true) },
        });
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Failed to create program");
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="p-6 sm:p-10 max-w-3xl mx-auto page-enter">
      <button onClick={() => nav(-1)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-6" data-testid="back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">New Program · Sales Opportunity</div>
      <h1 className="font-heading text-2xl font-semibold tracking-tight mt-1">Log a Training Opportunity</h1>
      <p className="text-sm text-muted-foreground mt-1">Submitting this form completes Step 1 (Sales Opportunity & Client Requirements), then hands off to Program Management for Step 2 (Faculty Mapping & IT Handoff).</p>

      <Card className="mt-8 border-border">
        <CardContent className="p-6">
          <form onSubmit={submit} className="grid gap-5">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground pt-2">Program</div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Client *">
                <Select value={form.client_name || ""} onValueChange={pickClient}>
                  <SelectTrigger data-testid="cp-client-select"><SelectValue placeholder="Select from Client directory..." /></SelectTrigger>
                  <SelectContent>
                    {clients.length === 0 && (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">No clients yet — add one under Admin &gt; Clients.</div>
                    )}
                    {clients.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}{c.primary_contact ? ` — ${c.primary_contact}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                {hasRole(user) && (
                  <div className="flex items-center justify-end">
                    <Link to="/admin/clients" target="_blank" className="text-xs text-primary whitespace-nowrap hover:underline">Manage clients</Link>
                  </div>
                )}
              </Field>
              <Field label="Training Title *">
                <Input data-testid="cp-training-title" required value={form.training_title} onChange={(e) => update("training_title", e.target.value)} placeholder="GenAI for Enterprise Engineers" />
              </Field>
            </div>
            <Field label="Training Topic / Domain *">
              <Input data-testid="cp-training-topic" required value={form.training_topic} onChange={(e) => update("training_topic", e.target.value)} placeholder="LLMs, RAG, Fine-tuning" />
            </Field>
            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="Batch Size">
                <Input data-testid="cp-batch-size" type="number" min={0} value={form.batch_size} onChange={(e) => update("batch_size", e.target.value)} />
              </Field>
              <Field label="Start Date">
                <Input data-testid="cp-start-date" type="date" value={form.start_date} onChange={(e) => update("start_date", e.target.value)} />
              </Field>
              <Field label="End Date">
                <Input data-testid="cp-end-date" type="date" value={form.end_date} onChange={(e) => update("end_date", e.target.value)} />
              </Field>
            </div>

            <div className="text-[11px] uppercase tracking-widest text-muted-foreground pt-4 border-t border-border">Sales Opportunity & Client Requirements (Step 1)</div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Client Contact Person">
                <Input data-testid="cp-client-contact" value={form.client_contact} onChange={(e) => update("client_contact", e.target.value)} placeholder="Jane Doe" />
              </Field>
              <Field label="Client Contact Email">
                <Input data-testid="cp-client-email" type="email" value={form.client_email} onChange={(e) => update("client_email", e.target.value)} placeholder="jane@client.com" />
              </Field>
            </div>
            <Field label="Estimated Revenue (INR)">
              <Input data-testid="cp-estimated-revenue" type="number" min={0} step="1" value={form.estimated_revenue} onChange={(e) => update("estimated_revenue", e.target.value)} placeholder="3000000" />
            </Field>
            <Field label="Requirements Summary">
              <Textarea data-testid="cp-requirements" rows={3} value={form.requirements_summary} onChange={(e) => update("requirements_summary", e.target.value)} placeholder="Client's context, learning goals, must-haves..." />
            </Field>
            <Field label="Skills / Technology">
              <MultiSelect
                testId="cp-technology"
                options={skillNames}
                value={form.technology}
                onChange={(next) => update("technology", next)}
              />
            </Field>
            <Field label="Delivery Mode">
              <Select value={form.delivery_mode || ""} onValueChange={(v) => update("delivery_mode", v)}>
                <SelectTrigger data-testid="cp-delivery-mode"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Online">Online</SelectItem>
                  <SelectItem value="Hybrid">Hybrid</SelectItem>
                  <SelectItem value="Onsite">Onsite</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Hours per Day">
                <Input data-testid="cp-hours-per-day" type="number" min={0} step="0.5" value={form.hours_per_day} onChange={(e) => update("hours_per_day", e.target.value)} placeholder="3" />
              </Field>
              <Field label="Total Duration (hours)">
                <Input data-testid="cp-duration-hours" type="number" min={0} step="0.5" value={form.duration_hours} onChange={(e) => update("duration_hours", e.target.value)} placeholder="Hours/day x days" />
              </Field>
              <Field label="Daily Time Slot Start">
                <TimeInput testId="cp-time-slot-start" value={form.time_slot_start} onChange={(v) => update("time_slot_start", v)} />
              </Field>
              <Field label="Daily Time Slot End">
                <TimeInput testId="cp-time-slot-end" value={form.time_slot_end} onChange={(v) => update("time_slot_end", v)} />
              </Field>
            </div>
            <p className="text-xs text-muted-foreground -mt-3">Hours per Day x days between Start and End Date must equal Total Duration.</p>

            <Field label="Additional Notes">
              <Textarea data-testid="cp-notes" rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Any additional details for PM..." />
            </Field>
            <Field label="Attachments (screenshots, Excel, PDF...)">
              <div>
                <input
                  id="cp-file-input"
                  type="file"
                  multiple
                  className="hidden"
                  data-testid="cp-file-input"
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                />
                <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById("cp-file-input").click()} data-testid="cp-file-pick">
                  <Upload className="w-4 h-4 mr-2" /> Attach files
                </Button>
                <div className="text-xs text-muted-foreground mt-1">Max 15 MB per file. Uploaded to Step 1 once the program is created.</div>
              </div>
              {files.length > 0 && (
                <div className="grid gap-1 mt-1">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border rounded-md px-3 py-1.5">
                      <span className="truncate">{f.name}</span>
                      <button type="button" onClick={() => removeFile(i)} className="text-muted-foreground hover:text-destructive" data-testid={`cp-file-remove-${i}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Field>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="outline" type="button" onClick={() => nav(-1)} data-testid="cp-cancel">Cancel</Button>
              <Button type="submit" disabled={loading} data-testid="cp-submit">{loading ? "Creating..." : "Create & Start Workflow"}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-widest text-muted-foreground block min-h-[2rem] leading-tight">{label}</Label>
      {children}
    </div>
  );
}
